/**
 * Client HTTP pour les routes `/admin/...` de l'API NestJS VVS — porté depuis
 * apps/web/lib/admin-api-client.ts. Même esprit que `apiFetch` dans
 * `./api-client.ts` (RFC 7807, base `/api/v1`), dédié au back-office admin :
 * rien n'est jamais mis en cache (`cache: "no-store"` partout).
 *
 * Mêmes trois changements que `./api-client.ts` (cf. son commentaire de tête
 * pour le détail) : base URL relative fixe, `credentials: "include"` partout,
 * et confirmation par lecture directe qu'aucun `next/headers` n'était importé
 * ici (l'original n'en importait déjà pas).
 *
 * Réutilise `ApiError`/`ApiProblemDetails`/`MoneyAmount` de `./api-client.ts`
 * tels quels (même format d'erreur RFC 7807 que le reste du projet) plutôt
 * que de les redéfinir.
 */

import { ApiError, type ApiProblemDetails, type MoneyAmount } from "./api-client";

const API_PREFIX = "/api/v1";

/** Cf. `buildRequestPath()` dans `./api-client.ts` pour la justification de ne pas utiliser le constructeur `URL`. */
function buildRequestPath(
  path: string,
  searchParams?: Record<string, string | number | boolean | undefined>,
): string {
  const query = new URLSearchParams();
  if (searchParams) {
    for (const [key, value] of Object.entries(searchParams)) {
      if (value !== undefined) query.set(key, String(value));
    }
  }
  const qs = query.toString();
  return `${API_PREFIX}${path}${qs ? `?${qs}` : ""}`;
}

export interface AdminUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  roles: string[];
}

export interface AdminLoginResult {
  accessToken: string;
  admin: AdminUser;
}

interface AdminRequestOptions {
  searchParams?: Record<string, string | number | boolean | undefined>;
  /**
   * Jeton d'accès admin — absent uniquement pour `POST /admin/auth/login`.
   * Devenu optionnel partout ailleurs (l'original le rendait obligatoire) :
   * une fois le cookie httpOnly admin posé directement par apps/api,
   * `credentials: "include"` suffit à authentifier la requête — ce paramètre
   * reste utilisable pour forcer un jeton explicite si un appelant futur en a
   * un en main, mais src/lib/auth/admin-auth-context.tsx n'en garde plus.
   */
  accessToken?: string;
}

async function adminApiFetch<T>(
  method: "GET" | "POST" | "PATCH" | "DELETE",
  path: string,
  body: unknown,
  options: AdminRequestOptions = {},
): Promise<T> {
  const requestPath = buildRequestPath(path, options.searchParams);

  // `FormData` (upload de fichier, cf. `uploadAdminProductMedia()`) : ne JAMAIS poser
  // `Content-Type` soi-même — le navigateur doit fixer le `boundary` multipart lui-même,
  // un en-tête manuel casserait le parsing côté API (`multer`). Body passé tel quel (pas
  // de `JSON.stringify`).
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined && !isFormData) headers["Content-Type"] = "application/json";
  if (options.accessToken) headers["Authorization"] = `Bearer ${options.accessToken}`;

  const res = await fetch(requestPath, {
    method,
    headers,
    body: body === undefined ? undefined : isFormData ? (body as FormData) : JSON.stringify(body),
    // Zone admin : aucune donnée n'est jamais mise en cache.
    cache: "no-store",
    credentials: "include",
  });

  if (!res.ok) {
    let problem: ApiProblemDetails | undefined;
    try {
      problem = (await res.json()) as ApiProblemDetails;
    } catch {
      problem = undefined;
    }
    throw new ApiError(
      res.status,
      problem,
      problem?.detail ??
        problem?.title ??
        `Échec de la requête API admin VVS : ${method} ${path} (HTTP ${res.status})`,
    );
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** `POST /admin/auth/login` — body `{ email, password }`. */
export function adminLogin(email: string, password: string): Promise<AdminLoginResult> {
  return adminApiFetch<AdminLoginResult>("POST", "/admin/auth/login", { email, password });
}

/** `GET /admin/auth/me` — authentifié via le cookie httpOnly admin. */
export function getCurrentAdminUser(accessToken?: string): Promise<AdminUser> {
  return adminApiFetch<AdminUser>("GET", "/admin/auth/me", undefined, { accessToken });
}

/** KPI de la page d'accueil du back-office (`GET /admin/dashboard/summary`). */
export interface AdminDashboardSummary {
  ordersToday: number;
  ordersThisWeek: number;
  revenueTodayMinor: MoneyAmount;
  pendingConfirmationCount: number;
  lowStockCount: number;
}

export function getAdminDashboardSummary(accessToken?: string): Promise<AdminDashboardSummary> {
  return adminApiFetch<AdminDashboardSummary>("GET", "/admin/dashboard/summary", undefined, { accessToken });
}

// ---------------------------------------------------------------------------
// Catalogue admin — produits, catégories, marques (apps/api/src/admin/admin-products.*,
// admin-categories.*, admin-brands.*). Limite assumée : un produit n'a ici qu'une seule
// variante ("par défaut"), pas d'éditeur multi-variantes ; pas de suppression
// catégorie/marque, cf. commentaires des services correspondants côté API.
// ---------------------------------------------------------------------------

export type AdminProductStatus = "DRAFT" | "ACTIVE" | "ARCHIVED";
export type AdminProductVisibility = "PUBLIC" | "CATALOG_ONLY" | "SEARCH_ONLY" | "HIDDEN";
export type AdminProductType = "SIMPLE" | "VARIABLE" | "BUNDLE" | "GIFT_CARD" | "SAMPLE";
export type AdminVariantStatus = "ACTIVE" | "DISCONTINUED";

export interface AdminEntityRef {
  id: string;
  name: string;
}

export interface AdminProductSummary {
  id: string;
  name: string;
  slug: string;
  skuRoot: string;
  status: AdminProductStatus;
  visibility: AdminProductVisibility;
  brand: AdminEntityRef | null;
  primaryCategory: AdminEntityRef | null;
  price: MoneyAmount | null;
  variantCount: number;
  updatedAt: string;
}

/** Stock en lecture seule (jamais écrit depuis cette fiche, cf. `/admin/stock`). `null` si aucune ligne `Inventory`. */
export interface AdminProductVariantStock {
  quantityOnHand: number;
  quantityAvailable: number;
}

export interface AdminProductVariant {
  id: string;
  sku: string;
  isDefault: boolean;
  status: AdminVariantStatus;
  price: MoneyAmount;
  compareAtPrice: MoneyAmount | null;
  costPrice: MoneyAmount | null;
  weightG: number | null;
  barcode: string | null;
  stock: AdminProductVariantStock | null;
}

export interface AdminProductTag {
  code: string;
  label: string;
}

export interface AdminProductMedia {
  /** Id `Media` — utilisé comme `:mediaId` par les routes `POST|DELETE|PATCH /admin/products/:id/media/:mediaId`. */
  id: string;
  url: string;
  alt: string | null;
  type: "IMAGE" | "VIDEO" | "DOCUMENT";
  isPrimary: boolean;
  position: number;
}

export interface AdminProductSeo {
  metaTitle: string | null;
  metaDescription: string | null;
}

export interface AdminProductDetail {
  id: string;
  name: string;
  subtitle: string | null;
  nameInternational: string | null;
  slug: string;
  skuRoot: string;
  type: AdminProductType;
  status: AdminProductStatus;
  visibility: AdminProductVisibility;
  descriptionShort: string | null;
  descriptionLong: string | null;
  usageInstructions: string | null;
  precautions: string | null;
  ageRestricted: boolean;
  brand: AdminEntityRef | null;
  primaryCategory: AdminEntityRef | null;
  tags: AdminProductTag[];
  media: AdminProductMedia[];
  seo: AdminProductSeo;
  /** Cf. limite de lot documentée en tête de section : une seule variante ici. */
  variants: AdminProductVariant[];
  createdAt: string;
  updatedAt: string;
}

export interface AdminPagePaginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface GetAdminProductsParams {
  search?: string;
  status?: AdminProductStatus;
  page?: number;
  pageSize?: number;
}

/** `GET /admin/products?search=&status=&page=&pageSize=` */
export function getAdminProducts(
  accessToken: string | undefined,
  params: GetAdminProductsParams = {},
): Promise<AdminPagePaginated<AdminProductSummary>> {
  return adminApiFetch<AdminPagePaginated<AdminProductSummary>>("GET", "/admin/products", undefined, {
    accessToken,
    searchParams: {
      search: params.search,
      status: params.status,
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

/** `GET /admin/products/:id` */
export function getAdminProductDetail(accessToken: string | undefined, id: string): Promise<AdminProductDetail> {
  return adminApiFetch<AdminProductDetail>("GET", `/admin/products/${id}`, undefined, { accessToken });
}

export interface CreateAdminProductInput {
  name: string;
  skuRoot: string;
  slug?: string;
  /** Prix (unité mineure XOF) de la variante par défaut créée avec le produit. */
  priceMinor: number;
  type?: AdminProductType;
  brandId?: string;
  primaryCategoryId?: string;
  descriptionShort?: string;
  descriptionLong?: string;
}

/** `POST /admin/products` — crée le produit ET sa variante par défaut. */
export function createAdminProduct(
  accessToken: string | undefined,
  input: CreateAdminProductInput,
): Promise<AdminProductDetail> {
  return adminApiFetch<AdminProductDetail>("POST", "/admin/products", input, { accessToken });
}

export interface UpdateAdminProductInput {
  name?: string;
  subtitle?: string;
  nameInternational?: string;
  descriptionShort?: string;
  descriptionLong?: string;
  usageInstructions?: string;
  precautions?: string;
  ageRestricted?: boolean;
  status?: AdminProductStatus;
  brandId?: string;
  primaryCategoryId?: string;
  /** Liste de tags séparés par virgule ou pipe — état COMPLET des tags (ajouts et retraits). */
  tags?: string;
  /** Prix (unité mineure XOF) de la variante par défaut, si présent. */
  priceMinor?: number;
  compareAtPriceMinor?: number;
  costPriceMinor?: number;
  weightG?: number;
  barcode?: string;
  metaTitle?: string;
  metaDescription?: string;
}

/** `PATCH /admin/products/:id` */
export function updateAdminProduct(
  accessToken: string | undefined,
  id: string,
  input: UpdateAdminProductInput,
): Promise<AdminProductDetail> {
  return adminApiFetch<AdminProductDetail>("PATCH", `/admin/products/${id}`, input, { accessToken });
}

/** `POST /admin/products/:id/media` — body `{ url, kind }` : télécharge et lie une nouvelle image ou vidéo. */
export function addAdminProductMedia(
  accessToken: string | undefined,
  id: string,
  url: string,
  kind: "image" | "video" = "image",
): Promise<AdminProductDetail> {
  return adminApiFetch<AdminProductDetail>("POST", `/admin/products/${id}/media`, { url, kind }, { accessToken });
}

/**
 * `POST /admin/products/:id/media` — variante fichier (champ multipart `file`, MÊME
 * endpoint que `addAdminProductMedia()` ci-dessus — le `FileInterceptor` côté API est un
 * no-op sur une requête JSON, donc les deux cohabitent). `multipart/form-data` construit
 * via `FormData` du navigateur (jamais `JSON.stringify` ni `Content-Type` posé à la main,
 * cf. `adminApiFetch()`).
 */
export function uploadAdminProductMedia(
  accessToken: string | undefined,
  id: string,
  file: File,
): Promise<AdminProductDetail> {
  const formData = new FormData();
  formData.append("file", file);
  return adminApiFetch<AdminProductDetail>("POST", `/admin/products/${id}/media`, formData, { accessToken });
}

/** `DELETE /admin/products/:id/media/:mediaId` */
export function removeAdminProductMedia(
  accessToken: string | undefined,
  id: string,
  mediaId: string,
): Promise<AdminProductDetail> {
  return adminApiFetch<AdminProductDetail>("DELETE", `/admin/products/${id}/media/${mediaId}`, undefined, {
    accessToken,
  });
}

/** `PATCH /admin/products/:id/media/:mediaId` — body `{ isPrimary: true }` */
export function setAdminProductMediaPrimary(
  accessToken: string | undefined,
  id: string,
  mediaId: string,
): Promise<AdminProductDetail> {
  return adminApiFetch<AdminProductDetail>(
    "PATCH",
    `/admin/products/${id}/media/${mediaId}`,
    { isPrimary: true },
    { accessToken },
  );
}

export interface AdminCategoryBanner {
  /** Id `Media` — utilisé par `removeAdminCategoryBanner()` uniquement pour l'affichage (pas de `:mediaId` dans l'URL, `bannerMediaId` est une colonne unique par catégorie). */
  id: string;
  url: string;
}

export interface AdminCategoryTreeNode {
  id: string;
  name: string;
  slug: string;
  path: string;
  level: number;
  /** Ordre parmi les frères/sœurs (même `parentId`) — cf. `moveAdminCategory()`. */
  position: number;
  isVisible: boolean;
  descriptionShort: string | null;
  descriptionSeo: string | null;
  ageRestricted: boolean;
  minAge: number | null;
  bannerMedia: AdminCategoryBanner | null;
  parentId: string | null;
  children: AdminCategoryTreeNode[];
}

/** `GET /admin/categories` — arbre complet, catégories masquées incluses. */
export function getAdminCategories(accessToken?: string): Promise<AdminCategoryTreeNode[]> {
  return adminApiFetch<AdminCategoryTreeNode[]>("GET", "/admin/categories", undefined, { accessToken });
}

export interface AdminCategory {
  id: string;
  parentId: string | null;
  name: string;
  slug: string;
  path: string;
  level: number;
  position: number;
  descriptionShort: string | null;
  descriptionSeo: string | null;
  ageRestricted: boolean;
  minAge: number | null;
  bannerMediaId: string | null;
  isVisible: boolean;
}

export interface CreateAdminCategoryInput {
  name: string;
  /** Absent = catégorie racine. */
  parentId?: string;
  slug?: string;
  descriptionShort?: string;
  descriptionSeo?: string;
  ageRestricted?: boolean;
  minAge?: number;
}

/** `POST /admin/categories` — `path`/`level` (ltree) calculés côté API depuis `parentId`. */
export function createAdminCategory(
  accessToken: string | undefined,
  input: CreateAdminCategoryInput,
): Promise<AdminCategory> {
  return adminApiFetch<AdminCategory>("POST", "/admin/categories", input, { accessToken });
}

export interface UpdateAdminCategoryInput {
  name?: string;
  isVisible?: boolean;
  descriptionShort?: string;
  descriptionSeo?: string;
  ageRestricted?: boolean;
  minAge?: number;
}

/** `PATCH /admin/categories/:id` — pas de suppression ni de réaffectation de parent à ce lot. */
export function updateAdminCategory(
  accessToken: string | undefined,
  id: string,
  input: UpdateAdminCategoryInput,
): Promise<AdminCategory> {
  return adminApiFetch<AdminCategory>("PATCH", `/admin/categories/${id}`, input, { accessToken });
}

/** `POST /admin/categories/:id/banner` — body `{ url }` : télécharge et lie une nouvelle bannière. Retourne l'arbre complet rafraîchi (même convention que `getAdminCategories()`). */
export function addAdminCategoryBanner(
  accessToken: string | undefined,
  id: string,
  url: string,
): Promise<AdminCategoryTreeNode[]> {
  return adminApiFetch<AdminCategoryTreeNode[]>("POST", `/admin/categories/${id}/banner`, { url }, { accessToken });
}

/**
 * `POST /admin/categories/:id/banner` — variante fichier (champ multipart `file`, MÊME
 * endpoint que `addAdminCategoryBanner()` ci-dessus, cf. `uploadAdminProductMedia()` pour
 * la même convention côté produits).
 */
export function uploadAdminCategoryBanner(
  accessToken: string | undefined,
  id: string,
  file: File,
): Promise<AdminCategoryTreeNode[]> {
  const formData = new FormData();
  formData.append("file", file);
  return adminApiFetch<AdminCategoryTreeNode[]>("POST", `/admin/categories/${id}/banner`, formData, { accessToken });
}

/** `DELETE /admin/categories/:id/banner` — délie la bannière (ne supprime pas la ligne `Media`). */
export function removeAdminCategoryBanner(
  accessToken: string | undefined,
  id: string,
): Promise<AdminCategoryTreeNode[]> {
  return adminApiFetch<AdminCategoryTreeNode[]>("DELETE", `/admin/categories/${id}/banner`, undefined, {
    accessToken,
  });
}

/** `POST /admin/categories/:id/move` — body `{ direction }` : échange de `position` avec le frère/sœur adjacent. */
export function moveAdminCategory(
  accessToken: string | undefined,
  id: string,
  direction: "up" | "down",
): Promise<AdminCategoryTreeNode[]> {
  return adminApiFetch<AdminCategoryTreeNode[]>("POST", `/admin/categories/${id}/move`, { direction }, { accessToken });
}

export interface AdminBrand {
  id: string;
  name: string;
  slug: string;
  countryOfOrigin: string | null;
  isFeatured: boolean;
  logoMedia: { id: string; url: string } | null;
}

/** `GET /admin/brands` */
export function getAdminBrands(accessToken?: string): Promise<AdminBrand[]> {
  return adminApiFetch<AdminBrand[]>("GET", "/admin/brands", undefined, { accessToken });
}

export interface CreateAdminBrandInput {
  name: string;
  slug?: string;
  isFeatured?: boolean;
  /** Code pays ISO 3166-1 alpha-2 (ex. "CI"). */
  countryOfOrigin?: string;
}

/** `POST /admin/brands` */
export function createAdminBrand(
  accessToken: string | undefined,
  input: CreateAdminBrandInput,
): Promise<AdminBrand> {
  return adminApiFetch<AdminBrand>("POST", "/admin/brands", input, { accessToken });
}

export interface UpdateAdminBrandInput {
  name?: string;
  isFeatured?: boolean;
  countryOfOrigin?: string;
}

/** `PATCH /admin/brands/:id` — pas de suppression à ce lot. */
export function updateAdminBrand(
  accessToken: string | undefined,
  id: string,
  input: UpdateAdminBrandInput,
): Promise<AdminBrand> {
  return adminApiFetch<AdminBrand>("PATCH", `/admin/brands/${id}`, input, { accessToken });
}

/** `POST /admin/brands/:id/logo` — body `{ url }` : télécharge et lie un nouveau logo. Retourne la liste complète rafraîchie (même convention que `addAdminCategoryBanner()`). */
export function addAdminBrandLogo(accessToken: string | undefined, id: string, url: string): Promise<AdminBrand[]> {
  return adminApiFetch<AdminBrand[]>("POST", `/admin/brands/${id}/logo`, { url }, { accessToken });
}

/** `POST /admin/brands/:id/logo` — variante fichier (champ multipart `file`, MÊME endpoint que `addAdminBrandLogo()`). */
export function uploadAdminBrandLogo(accessToken: string | undefined, id: string, file: File): Promise<AdminBrand[]> {
  const formData = new FormData();
  formData.append("file", file);
  return adminApiFetch<AdminBrand[]>("POST", `/admin/brands/${id}/logo`, formData, { accessToken });
}

/** `DELETE /admin/brands/:id/logo` — délie le logo (ne supprime pas la ligne `Media`). */
export function removeAdminBrandLogo(accessToken: string | undefined, id: string): Promise<AdminBrand[]> {
  return adminApiFetch<AdminBrand[]>("DELETE", `/admin/brands/${id}/logo`, undefined, { accessToken });
}

// ---------------------------------------------------------------------------
// GET /admin/orders · GET /admin/orders/:id · POST /admin/orders/:id/notes ·
// POST /admin/orders/:id/confirm · POST /admin/orders/:id/cancel. Formes
// confirmées par lecture directe de apps/api/src/admin/admin-orders.controller.ts
// et admin-orders.service.ts (`toSummary()`/`toDetail()`/`toEvent()`).
// ---------------------------------------------------------------------------

export type AdminOrderStatus =
  | "DRAFT"
  | "PENDING_CONFIRMATION"
  | "CONFIRMED"
  | "AWAITING_PAYMENT"
  | "PAID"
  | "PROCESSING"
  | "READY_TO_SHIP"
  | "SHIPPED"
  | "DELIVERY_FAILED"
  | "DELIVERED"
  | "RETURN_REQUESTED"
  | "RETURNED"
  | "REFUNDED"
  | "COMPLETED"
  | "CANCELLED"
  | "RETURNED_TO_SENDER";

/** Cf. `CancellationReason` (packages/db/prisma/schema.prisma). */
export type AdminCancellationReason =
  | "CUSTOMER_REQUEST"
  | "UNREACHABLE"
  | "OUT_OF_STOCK"
  | "FRAUD_SUSPECT"
  | "DELIVERY_FAILED";

/** Forme exacte de `AdminOrdersService.toSummary()`. */
export interface AdminOrderSummary {
  id: string;
  orderNumber: string;
  status: AdminOrderStatus;
  paymentStatus: string;
  channel: string;
  customerId: string | null;
  placedAt: string;
  confirmedAt: string | null;
  cancelledAt: string | null;
  cancellationReason: AdminCancellationReason | null;
  confirmationAttempts: number;
  grandTotal: MoneyAmount;
}

/** Forme exacte de `PagePaginatedResponseDto<AdminOrderSummary>`. */
export interface AdminOrdersListResponse {
  items: AdminOrderSummary[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

export interface GetAdminOrdersParams {
  status?: AdminOrderStatus;
  /** Recherche libre : matche `orderNumber` ou le snapshot d'adresse (nom/téléphone du destinataire). */
  search?: string;
  page?: number;
  pageSize?: number;
  /** Index signature requise pour la compatibilité structurelle avec `AdminRequestOptions.searchParams`. */
  [key: string]: string | number | boolean | undefined;
}

/** `GET /admin/orders?status=&search=&page=&pageSize=` — VVS_MODELE_INTERFACE.md §15. */
export function getAdminOrders(
  accessToken: string | undefined,
  params: GetAdminOrdersParams = {},
): Promise<AdminOrdersListResponse> {
  return adminApiFetch<AdminOrdersListResponse>("GET", "/admin/orders", undefined, {
    accessToken,
    searchParams: params,
  });
}

export interface AdminOrderDetailTotals {
  subtotal: MoneyAmount;
  discount: MoneyAmount;
  shipping: MoneyAmount;
  tax: MoneyAmount;
  grandTotal: MoneyAmount;
  paid: MoneyAmount;
  refunded: MoneyAmount;
}

export interface AdminOrderDetailItem {
  id: string;
  sku: string;
  productName: string;
  variantName: string | null;
  brandName: string | null;
  imageUrl: string | null;
  quantity: number;
  fulfilledQuantity: number;
  returnedQuantity: number;
  unitPrice: MoneyAmount;
  discount: MoneyAmount;
  tax: MoneyAmount;
  lineTotal: MoneyAmount;
}

/** Forme exacte de `AdminOrdersService.toEvent()`. `payload` reste libre (varie selon `type`, ex. `{ note }` pour NOTE, `{ outcome, attempt }` pour CALL_ATTEMPT). */
export interface AdminOrderEvent {
  id: string;
  type: string;
  fromStatus: AdminOrderStatus | null;
  toStatus: AdminOrderStatus | null;
  payload: Record<string, unknown>;
  actorType: string;
  actorId: string | null;
  occurredAt: string;
}

/** Snapshot immuable de l'adresse (§8.2) — champs bruts, non garantis au-delà de `unknown`. */
export interface AdminOrderShippingAddress {
  recipientName?: unknown;
  phoneE164?: unknown;
  addressLine?: unknown;
  quartier?: unknown;
  landmark?: unknown;
  [key: string]: unknown;
}

/** Forme exacte de `AdminOrdersService.toDetail()`. */
export interface AdminOrderDetail {
  id: string;
  orderNumber: string;
  vendorId: string;
  customerId: string | null;
  status: AdminOrderStatus;
  paymentStatus: string;
  fulfillmentStatus: string;
  channel: string;
  currency: string;
  couponCode: string | null;
  shippingAddress: AdminOrderShippingAddress | null;
  billingAddress: AdminOrderShippingAddress | null;
  shippingMethodCode: string | null;
  customerNote: string | null;
  internalNote: string | null;
  placedAt: string;
  confirmedAt: string | null;
  confirmedByUserId: string | null;
  confirmationAttempts: number;
  shippedAt: string | null;
  deliveredAt: string | null;
  cancelledAt: string | null;
  cancellationReason: AdminCancellationReason | null;
  totals: AdminOrderDetailTotals;
  items: AdminOrderDetailItem[];
  events: AdminOrderEvent[];
}

/** `GET /admin/orders/:id`. */
export function getAdminOrderDetail(accessToken: string | undefined, id: string): Promise<AdminOrderDetail> {
  return adminApiFetch<AdminOrderDetail>("GET", `/admin/orders/${encodeURIComponent(id)}`, undefined, {
    accessToken,
  });
}

/** `POST /admin/orders/:id/notes` — crée un `OrderEvent` NOTE, sans transition de statut ; renvoie l'événement créé. */
export function addAdminOrderNote(
  accessToken: string | undefined,
  id: string,
  note: string,
): Promise<AdminOrderEvent> {
  return adminApiFetch<AdminOrderEvent>(
    "POST",
    `/admin/orders/${encodeURIComponent(id)}/notes`,
    { note },
    { accessToken },
  );
}

/** Cf. `CallAttemptOutcome` (dto/confirm-order.dto.ts). */
export type AdminCallAttemptOutcome = "CONFIRMED" | "UNREACHABLE";

/**
 * `POST /admin/orders/:id/confirm` — bouton "Confirmée" / "Injoignable" de la file de
 * confirmation téléphonique. `outcome` par défaut `CONFIRMED` côté API si omis.
 */
export function confirmAdminOrder(
  accessToken: string | undefined,
  id: string,
  outcome?: AdminCallAttemptOutcome,
): Promise<AdminOrderSummary> {
  return adminApiFetch<AdminOrderSummary>(
    "POST",
    `/admin/orders/${encodeURIComponent(id)}/confirm`,
    outcome ? { outcome } : {},
    { accessToken },
  );
}

/** `POST /admin/orders/:id/cancel` — bouton "Annulée" de la file. */
export function cancelAdminOrder(
  accessToken: string | undefined,
  id: string,
  reason: AdminCancellationReason,
  note?: string,
): Promise<AdminOrderSummary> {
  return adminApiFetch<AdminOrderSummary>(
    "POST",
    `/admin/orders/${encodeURIComponent(id)}/cancel`,
    { reason, ...(note ? { note } : {}) },
    { accessToken },
  );
}

/** Réponse paginée par page (back-office) — même forme que `PagePaginatedResponseDto` côté API. */
export interface AdminPagePaginatedResponse<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

/** Ligne de `GET /admin/inventory` (jointure inventory + variante + produit + entrepôt). */
export interface AdminInventoryRow {
  inventoryId: string;
  variantId: string;
  sku: string;
  productId: string;
  productName: string;
  nameSuffix: string | null;
  warehouseId: string;
  warehouseName: string;
  quantityOnHand: number;
  quantityReserved: number;
  quantityAvailable: number;
  lowStockThreshold: number;
  isLowStock: boolean;
}

/** `GET /admin/inventory?lowStockOnly=&search=&page=&pageSize=`. */
export function getAdminInventory(
  accessToken: string | undefined,
  params: { lowStockOnly?: boolean; search?: string; page?: number; pageSize?: number } = {},
): Promise<AdminPagePaginatedResponse<AdminInventoryRow>> {
  return adminApiFetch<AdminPagePaginatedResponse<AdminInventoryRow>>("GET", "/admin/inventory", undefined, {
    accessToken,
    searchParams: {
      lowStockOnly: params.lowStockOnly,
      search: params.search,
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

/** Résultat de `POST /admin/inventory/:variantId/adjust`. */
export interface AdminInventoryAdjustResult {
  inventoryId: string;
  variantId: string;
  warehouseId: string;
  quantityOnHand: number;
  quantityReserved: number;
  quantityAvailable: number;
  lowStockThreshold: number;
}

/** `POST /admin/inventory/:variantId/adjust` — body `{ warehouseId, quantityDelta, reason }`. */
export function adjustAdminInventory(
  accessToken: string | undefined,
  variantId: string,
  payload: { warehouseId: string; quantityDelta: number; reason: string },
): Promise<AdminInventoryAdjustResult> {
  return adminApiFetch<AdminInventoryAdjustResult>("POST", `/admin/inventory/${variantId}/adjust`, payload, {
    accessToken,
  });
}

/** Statuts possibles de `Customer.status` (clients boutique — distinct de `AdminUser`). */
export type AdminCustomerStatus = "ACTIVE" | "BLOCKED" | "GUEST";

/** Ligne de `GET /admin/customers` (liste/recherche). */
export interface AdminCustomerListItem {
  id: string;
  phoneE164: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  status: AdminCustomerStatus;
  ordersCount: number;
  lifetimeValue: MoneyAmount;
  lastOrderAt: string | null;
  createdAt: string;
}

/** Commande résumée dans l'historique du détail client. */
export interface AdminCustomerOrderSummary {
  id: string;
  orderNumber: string;
  status: string;
  paymentStatus: string;
  placedAt: string;
  itemsCount: number;
  total: MoneyAmount;
  thumbnailUrl: string | null;
}

/** `GET /admin/customers/:id` — détail complet + historique de commandes. */
export interface AdminCustomerDetail extends AdminCustomerListItem {
  phoneVerifiedAt: string | null;
  emailVerifiedAt: string | null;
  gender: string | null;
  birthDate: string | null;
  locale: string;
  acceptsMarketingSms: boolean;
  acceptsMarketingEmail: boolean;
  acceptsMarketingWhatsapp: boolean;
  orders: AdminCustomerOrderSummary[];
}

/** `GET /admin/customers?search=&status=&page=&pageSize=`. */
export function getAdminCustomers(
  accessToken: string | undefined,
  params: { search?: string; status?: AdminCustomerStatus; page?: number; pageSize?: number } = {},
): Promise<AdminPagePaginatedResponse<AdminCustomerListItem>> {
  return adminApiFetch<AdminPagePaginatedResponse<AdminCustomerListItem>>("GET", "/admin/customers", undefined, {
    accessToken,
    searchParams: {
      search: params.search,
      status: params.status,
      page: params.page,
      pageSize: params.pageSize,
    },
  });
}

/** `GET /admin/customers/:id`. */
export function getAdminCustomerDetail(accessToken: string | undefined, id: string): Promise<AdminCustomerDetail> {
  return adminApiFetch<AdminCustomerDetail>("GET", `/admin/customers/${id}`, undefined, { accessToken });
}

/** `POST /admin/customers/:id/block`. */
export function blockAdminCustomer(accessToken: string | undefined, id: string): Promise<AdminCustomerDetail> {
  return adminApiFetch<AdminCustomerDetail>("POST", `/admin/customers/${id}/block`, undefined, { accessToken });
}

/** `POST /admin/customers/:id/unblock`. */
export function unblockAdminCustomer(accessToken: string | undefined, id: string): Promise<AdminCustomerDetail> {
  return adminApiFetch<AdminCustomerDetail>("POST", `/admin/customers/${id}/unblock`, undefined, { accessToken });
}

// ---------------------------------------------------------------------------
// Paramètres (setting) — rend éditable depuis l'admin tout ce qui est
// "changeable" dans le front (bandeau d'annonce, contact, horaires…), au lieu
// de nécessiter un redéploiement. Cf. apps/api/src/admin/admin-settings.service.ts.
// ---------------------------------------------------------------------------

export interface AdminSetting {
  id: string;
  key: string;
  value: unknown;
  scope: string | null;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
}

/** `GET /admin/settings?scope=`. */
export function getAdminSettings(accessToken: string | undefined, scope?: string): Promise<AdminSetting[]> {
  return adminApiFetch<AdminSetting[]>("GET", "/admin/settings", undefined, {
    accessToken,
    searchParams: { scope },
  });
}

/** `POST /admin/settings`. */
export function createAdminSetting(
  accessToken: string | undefined,
  payload: { key: string; value: unknown; scope?: string; isPublic?: boolean },
): Promise<AdminSetting> {
  return adminApiFetch<AdminSetting>("POST", "/admin/settings", payload, { accessToken });
}

/** `PATCH /admin/settings/:key`. */
export function updateAdminSetting(
  accessToken: string | undefined,
  key: string,
  payload: { value?: unknown; scope?: string; isPublic?: boolean },
): Promise<AdminSetting> {
  return adminApiFetch<AdminSetting>("PATCH", `/admin/settings/${encodeURIComponent(key)}`, payload, { accessToken });
}
